import Hotjar from '@hotjar/browser';
import { get, type Readable, type Unsubscriber } from 'svelte/store';
import { app } from '$lib/stores/app/store';
import { experiments, type ExperimentAssignment } from '$lib/stores/experiments/store';
import { ENVIRONMENTS, SENTRY_ERROR_LEVELS } from '$lib/utils/constants';
import { getDeviceId } from '$lib/utils/device-id';
import { canLoadMarketingVendors } from '$lib/stores/app/actions';
import { Sentry } from '$lib/services/sentry';
import { user } from '$lib/stores/user/store';
import type { App } from '$lib/stores/app/types';
import type { UserStoreState } from '$lib/stores/user/store';

export type HotjarSdk = typeof Hotjar;

export type HotjarServiceConfig = {
	siteId: number;
	snippetVersion: number;
};

export type HotjarLogger = {
	fatal: (error: unknown) => void;
};

export type HotjarAttributes = Record<string, string | boolean>;

export type HotjarServiceDependencies = {
	app: Readable<App.AppStore>;
	canLoadMarketingVendors: () => boolean;
	config: HotjarServiceConfig;
	experiments: Readable<ExperimentAssignment[]>;
	getDeviceId: () => string | null;
	logger: HotjarLogger;
	sdk: HotjarSdk;
	user: Readable<UserStoreState>;
};

/** Must match the recording trigger configured in Hotjar Site settings. */
const START_CAPTURE_EVENT = 'start_capture';

const EXPERIMENT_ARM_EVENT_PREFIX = 'exp_';

const EXPERIMENT_ARM_EVENT_SEPARATOR = '__';

/** Hotjar rejects event names containing anything outside this set. */
const HOTJAR_EVENT_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * Call `initialize()` once the app store and device id exist, and
 * `stateChange()` on SPA navigations. Login refreshes identity from the user
 * store, and a consent revocation stops further calls.
 */
export class HotjarService {
	private readonly app: Readable<App.AppStore>;
	private readonly canLoadMarketingVendors: () => boolean;
	private readonly config: HotjarServiceConfig;
	private readonly experiments: Readable<ExperimentAssignment[]>;
	private readonly getDeviceId: () => string | null;
	private readonly logger: HotjarLogger;
	private readonly sdk: HotjarSdk;
	private readonly user: Readable<UserStoreState>;

	private initialized = false;
	private disabled = false;
	private lastIdentity = '';
	private readonly sentEventNames = new Set<string>();
	private subscriptions: Unsubscriber[] = [];

	constructor({
		app,
		canLoadMarketingVendors,
		config,
		experiments,
		getDeviceId,
		logger,
		sdk,
		user,
	}: HotjarServiceDependencies) {
		this.app = app;
		this.canLoadMarketingVendors = canLoadMarketingVendors;
		this.config = config;
		this.experiments = experiments;
		this.getDeviceId = getDeviceId;
		this.logger = logger;
		this.sdk = sdk;
		this.user = user;
	}

	public initialize(): void {
		if (this.initialized || this.disabled) return;
		if (!this.isEligible()) return;

		try {
			const initialized = this.sdk.init(this.config.siteId, this.config.snippetVersion);
			if (!initialized) return;
		} catch (error) {
			this.logger.fatal(error);
			return;
		}

		this.initialized = true;
		this.startTracking();
	}

	/**
	 * Hotjar attributes every virtual pageview to the URL it saw at `init`
	 * unless told otherwise. Skip the empty string so a missing `to.url`
	 * cannot overwrite a real path. The query string is dropped so tokens
	 * and other parameters never reach Hotjar.
	 */
	public stateChange(relativePath: string): void {
		const pathname = relativePath.split('?')[0];
		if (!pathname || !this.canCallSdk()) return;

		try {
			this.sdk.stateChange(pathname);
		} catch (error) {
			this.logger.fatal(error);
		}
	}

	/** A recording already in progress keeps running; the SDK cannot end one. */
	public disableService(): void {
		this.disabled = true;
		this.unsubscribe();
	}

	/** Production only, so review and dev apps cannot spend paid session quota. */
	private isEligible(): boolean {
		return (
			this.canLoadMarketingVendors() && get(this.app).meta.environment === ENVIRONMENTS.PRODUCTION
		);
	}

	/** `isReady` confirms the snippet's command queue exists; without it the SDK throws. */
	private canCallSdk(): boolean {
		return this.initialized && !this.disabled && this.sdk.isReady();
	}

	/**
	 * Re-sends when login or app context changes the payload. The visitor id
	 * stays the device id, so a later call updates filters without splitting
	 * the Hotjar user.
	 */
	private identifyVisitor(): void {
		if (!this.canCallSdk()) return;

		const userId = this.getUserId();
		const attributes = this.getAttributes();
		const identity = JSON.stringify([userId, attributes]);
		if (identity === this.lastIdentity) return;

		try {
			this.sdk.identify(userId, attributes);
			this.lastIdentity = identity;
		} catch (error) {
			this.logger.fatal(error);
		}
	}

	/** Login and app-context changes refresh filters. Lost consent ends tracking. */
	private syncIdentity(): void {
		if (!this.canLoadMarketingVendors()) {
			this.disableService();
			return;
		}

		this.identifyVisitor();
	}

	/**
	 * The device ID, never the account UUID. Hotjar keys a visitor by whatever ID
	 * it was last given, so an ID that changes once the account validates would
	 * file the pre-login and post-login pageloads under two different Hotjar
	 * users. The account is a filterable attribute instead.
	 */
	private getUserId(): string | null {
		return this.getDeviceId() || null;
	}

	/**
	 * Only what Hotjar cannot infer from the user agent: which app shell the
	 * session runs in and how it got here. Optional facets stay absent rather
	 * than empty so filters don't offer a meaningless blank value.
	 */
	private getAttributes(): HotjarAttributes {
		const { global, meta } = get(this.app);
		const { accountStatus, userToProductUuid } = get(this.user);
		const attributes: HotjarAttributes = {
			in_app: Boolean(meta.inApp),
			is_logged_in: Boolean(userToProductUuid),
			is_onn_device: Boolean(meta.isOnnDevice),
			link_from_app: Boolean(global.isLinkFromApp),
		};

		if (accountStatus) attributes.account_status = accountStatus;
		if (global.appProduct) attributes.app_product = global.appProduct;
		if (meta.appStore) attributes.app_store = meta.appStore;
		if (global.linkFromAppPlatform) attributes.link_from_app_platform = global.linkFromAppPlatform;
		if (userToProductUuid) attributes.user_to_product_uuid = userToProductUuid;

		return attributes;
	}

	private startCapture(): void {
		this.sendEvent(START_CAPTURE_EVENT);
	}

	/**
	 * The experiment subscription fires on every assignment write, and each send
	 * lands on the recording timeline.
	 */
	private sendEvent(eventName: string): void {
		if (this.sentEventNames.has(eventName)) return;

		this.sdk.event(eventName);
		this.sentEventNames.add(eventName);
	}

	/**
	 * Store subscriptions fire immediately, so this also sends the first
	 * identify. Identity goes first so experiment events land on an identified
	 * visitor.
	 */
	private startTracking(): void {
		this.subscribe(this.user, () => this.syncIdentity());
		this.subscribe(this.app, () => this.syncIdentity());
		this.subscribe(this.experiments, (assignments) => this.trackAssignedExperiments(assignments));
	}

	private subscribe<T>(source: Readable<T>, onChange: (value: T) => void): void {
		if (this.disabled) return;

		const stop = source.subscribe(onChange);
		if (this.disabled) {
			stop();
			return;
		}

		this.subscriptions.push(stop);
	}

	private unsubscribe(): void {
		const stops = this.subscriptions;
		this.subscriptions = [];
		for (const stop of stops) stop();
	}

	/**
	 * Runs inside a store subscriber, so a throw would surface in whichever code
	 * assigned the experiment.
	 */
	private trackAssignedExperiments(assignments: ExperimentAssignment[]): void {
		if (!this.canCallSdk()) return;

		const assigned = assignments.filter(
			(assignment: ExperimentAssignment) =>
				assignment.assigned !== false && assignment.hotjar === true
		);
		if (assigned.length === 0) return;

		try {
			this.startCapture();

			for (const assignment of assigned) {
				const eventName = this.getExperimentArmEventName(assignment);
				if (eventName === null) continue;

				this.sendEvent(eventName);
			}
		} catch (error) {
			this.logger.fatal(error);
		}
	}

	/**
	 * Periods are legal in experiment keys but not in Hotjar event names, so they
	 * become underscores. An empty variant — the `upsertExperiments` default —
	 * names no arm, so it yields null rather than a trailing-separator name.
	 */
	private getExperimentArmEventName(assignment: ExperimentAssignment): string | null {
		const { key, variant } = assignment;
		if (typeof key !== 'string' || typeof variant !== 'string') return null;
		if (key === '' || variant === '') return null;

		const eventName = `${EXPERIMENT_ARM_EVENT_PREFIX}${key.replace(/\./g, '_')}${EXPERIMENT_ARM_EVENT_SEPARATOR}${variant}`;

		return HOTJAR_EVENT_NAME_PATTERN.test(eventName) ? eventName : null;
	}
}

export const hotjarService = new HotjarService({
	app,
	canLoadMarketingVendors,
	config: {
		siteId: __HOTJAR_SITE_ID__,
		snippetVersion: __HOTJAR_SNIPPET_VERSION__,
	},
	experiments,
	getDeviceId,
	logger: {
		fatal: (error) => {
			Sentry.captureException(error, { level: SENTRY_ERROR_LEVELS.FATAL });
		},
	},
	sdk: Hotjar,
	user,
});
